import { Fragment, useState, useMemo, useCallback, useRef } from "react";
import {
  AlertCircle,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  History,
  Loader2,
  Package,
  Pencil,
  RotateCcw,
  Truck,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCheckOrders } from "@/features/purchase-orders/hooks/useCheckOrders";
import { useOrderMutations } from "@/features/purchase-orders/hooks/useOrderMutations";
import { toast } from "@/hooks/use-toast";
import { trpc } from "@/lib/trpc";
import { reportError } from "@/lib/logging";
import { toCsvContent } from "@/lib/csv";
import { cn } from "@/lib/utils";
import { useScannerAutoFocus } from "@/hooks/useScannerAutoFocus";
import { ReceivingScanInput, type ReceivingScanResult } from "@/features/purchase-orders/components/ReceivingScanInput";
import { AddUnexpectedItemPopover } from "@/features/purchase-orders/components/AddUnexpectedItemPopover";
import {
  receivingBaseline,
  shippedQuantitiesAreAuthoritative,
  shortfallUnits,
  unconfirmedShortfallLines,
  unreceivedShippedLines,
} from "@/features/purchase-orders/utils/receiving-baseline";
import { inTransitUnits, type UnreceivedResolution } from "@threadheads/shared";
import type { PurchaseOrder, PurchaseOrderItem } from "@/features/purchase-orders";

interface ReceivingCardProps {
  order: PurchaseOrder;
  items: PurchaseOrderItem[];
  /** Name of the transfer's source location, for the returned-to-source dialog. */
  sourceLocationName?: string | null;
  /** Whether the viewer may mark missing units returned or lost (PO manage permission). */
  canResolveUnreceived: boolean;
}

const TOLERANCE_THRESHOLD = 0.05; // 5%

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

export default function ReceivingCard({ order, items, sourceLocationName, canResolveUnreceived }: ReceivingCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, string>>({});
  const [shippedQuantities, setShippedQuantities] = useState<Record<string, string>>(() => {
    // Initialize from existing shipped data
    const initial: Record<string, string> = {};
    for (const item of items) {
      if ((item.shipped_quantity ?? 0) > 0) {
        initial[item.id] = String(item.shipped_quantity);
      }
    }
    return initial;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllVariances, setShowAllVariances] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
  const [revertConfirmItem, setRevertConfirmItem] = useState<PurchaseOrderItem | null>(null);
  const [bulkRevertConfirmOpen, setBulkRevertConfirmOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<{
    item: PurchaseOrderItem;
    resolution: UnreceivedResolution;
  } | null>(null);
  // BLU-584: transient highlight on the last scanned/allocated row.
  const [flashItemId, setFlashItemId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const flashTimeoutRef = useRef<number | null>(null);

  const {
    processItem,
    updateShippedQuantities,
    isSavingShipped,
    reEditItem,
    isReEditing,
    revertItem,
    revertAllItems,
    isReverting,
    addSurpriseItem,
    isAddingSurpriseItem,
    confirmUnshipped,
    isConfirmingUnshipped,
    resolveUnreceived,
    isResolvingUnreceived,
  } = useCheckOrders();
  const { updateOrderStatus } = useOrderMutations();
  const utils = trpc.useUtils();

  // BLU-377: receiving is independent of payment status. A PO can be paid
  // (deposit, pay-on-order) AND still have stock arrive. The DB trigger
  // protects exchange_rate from edits while paid; receive flow only writes
  // received_quantity + WAC, neither of which touches the rate.
  const canReceive = order.status !== "draft" && order.status !== "cancelled";

  // BLU-516: mirrors the backend's RECEIVABLE_STATUSES for addSurpriseItem —
  // narrower than canReceive because adding an unexpected line only makes
  // sense once the order has actually shipped.
  const canAddSurpriseItem =
    order.status === "submitted" ||
    order.status === "in_transit" ||
    order.status === "partially_received";

  const existingSkuIds = useMemo(
    () => new Set(items.map((i) => (i as { sku_id?: string | null }).sku_id).filter((id): id is string => !!id)),
    [items]
  );

  const handleAddSurpriseItem = async (skuId: string, quantity: number) => {
    await addSurpriseItem(order.id, skuId, quantity);
    toast({
      title: "Unexpected item added",
      description: `${quantity.toLocaleString()} unit${quantity === 1 ? "" : "s"} added to the order, ready to receive.`,
    });
  };

  // A line whose missing units were marked returned or scrapped cannot be
  // reverted (the server refuses it), so it is neither counted nor offered.
  const revertableItems = useMemo(() =>
    items.filter((i) =>
      (i.processing_status === 'add_stock' || i.processing_status === 'edited') &&
      (i.received_quantity ?? 0) > 0 &&
      i.unreceived_resolved_at == null
    ),
    [items]
  );
  const revertableCount = revertableItems.length;
  const revertableTotalUnits = revertableItems.reduce(
    (sum, i) => sum + ((i as { inventory_adjustment?: number | null }).inventory_adjustment ?? i.received_quantity ?? 0), 0
  );

  const orderLocationId = order.location_id;

  const handleRevertItem = async (item: PurchaseOrderItem) => {
    try {
      await revertItem(order.id, item.id, orderLocationId);
      setRevertConfirmItem(null);
      toast({
        title: "Item Reverted",
        description: `${item.sku}: ${(item as { inventory_adjustment?: number | null }).inventory_adjustment ?? item.received_quantity} units deducted from inventory`,
      });
    } catch (error) {
      reportError("Error reverting item", error);
    }
  };

  const handleRevertAll = async () => {
    try {
      // The all-lines revert refuses the whole order while any line is
      // resolved, so with one present it names the lines this button lists.
      const hasResolvedLine = items.some((i) => i.unreceived_resolved_at != null);
      const result = await revertAllItems(
        order.id,
        orderLocationId,
        hasResolvedLine ? revertableItems.map((i) => i.id) : undefined
      );
      setBulkRevertConfirmOpen(false);
      const count = (result as { revertedCount?: number })?.revertedCount ?? revertableCount;
      toast({
        title: "Items Reverted",
        description: `${count} item${count !== 1 ? 's' : ''} reverted: ${revertableTotalUnits} units deducted from inventory`,
      });
    } catch (error) {
      reportError("Error reverting items", error);
    }
  };

  // BLU-535 section D: the Shipped tab is packing-list data entry, a record of
  // what someone SAID was sent. Once a transfer has shipped per SKU that number
  // is the count of units that physically left the source, and the committed
  // balance, the receive cap and the completion gate all read it. Editing it
  // here would move no stock while changing every one of those answers, so the
  // tab comes off for a stamped transfer. The server refuses the write too.
  const shippedQuantitiesRecorded = Boolean(order.shipped_quantities_recorded_at);
  const shippedTabEditable = !(order.order_type === "transfer" && shippedQuantitiesRecorded);

  // Determine default tab based on PO status
  const defaultTab = useMemo(() => {
    if (order.status === "partially_received" || order.status === "received") return "received";
    if (order.status === "in_transit" || order.status === "ready") {
      return shippedTabEditable ? "shipped" : "received";
    }
    return "ordered";
  }, [order.status, shippedTabEditable]);

  // The tab set is not fixed: Shipped comes off the moment a transfer records
  // its shipped quantities. Uncontrolled Tabs kept "shipped" selected after
  // that trigger and its content unmounted, so the operator was left staring at
  // an empty panel with no tab highlighted. Controlled, with the selection
  // validated against what is actually on screen, a tab that disappears hands
  // the view back to the status default instead of to nothing.
  const availableTabs = useMemo(
    () => (shippedTabEditable ? ["ordered", "shipped", "received"] : ["ordered", "received"]),
    [shippedTabEditable]
  );
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const fallbackTab = availableTabs.includes(defaultTab) ? defaultTab : "received";
  const activeTab =
    selectedTab !== null && availableTabs.includes(selectedTab) ? selectedTab : fallbackTab;

  // ── Shipped tab helpers ──

  const parsedShippedQuantities = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of items) {
      const raw = shippedQuantities[item.id];
      if (raw === undefined || raw === "") continue;
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num >= 0) {
        map[item.id] = num;
      }
    }
    return map;
  }, [items, shippedQuantities]);

  const shippedDirty = useMemo(() => {
    return items.some((item) => {
      const current = parsedShippedQuantities[item.id] ?? 0;
      const saved = item.shipped_quantity ?? 0;
      return current !== saved;
    });
  }, [items, parsedShippedQuantities]);

  const handleShippedInputChange = (itemId: string, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setShippedQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleFillFromOrdered = () => {
    const next: Record<string, string> = {};
    for (const item of items) {
      next[item.id] = String(item.ordered_quantity);
    }
    setShippedQuantities(next);
  };

  const handleSaveShipped = async () => {
    const itemUpdates = items.map((item) => ({
      itemId: item.id,
      shippedQuantity: parsedShippedQuantities[item.id] ?? item.shipped_quantity ?? 0,
    }));

    try {
      await updateShippedQuantities(order.id, itemUpdates);
      toast({
        title: "Shipped quantities saved",
        description: "Packing list data has been recorded.",
      });
    } catch {
      // Error toast is handled by the mutation hook
    }
  };

  // ── Received tab helpers (existing logic) ──

  // BLU-1133 (D-01): "was a packing list recorded" is an order-level question.
  // The column default is 0 and the backend mapper flattens null to 0, so a
  // per-line test cannot tell an unrecorded line from a genuinely zero one.
  //
  // BLU-535 section D: a transfer that recorded its shipped quantities answers
  // that question outright, which also covers the case the packing-list
  // heuristic gets backwards, a transfer where every line shipped zero.
  const packingListEntered = useMemo(
    () => shippedQuantitiesAreAuthoritative(order, items),
    [order, items]
  );

  // One shortfall rule for the whole card: the per row confirm control below,
  // the header total and the complete button on the order page all read this
  // same helper, so they cannot disagree about what counts as a shortfall.
  const shortfallLines = useMemo(
    () => unconfirmedShortfallLines(order, items),
    [order, items]
  );
  // The confirm mutation only accepts a transfer that is still on its way, so
  // the control is offered only then. A cancelled transfer already released its
  // held committed units and a received one is finished: on either, the button
  // could only fire and bounce off the server.
  const canConfirmUnshipped =
    order.status === "in_transit" || order.status === "partially_received";
  const shortfallItemIds = useMemo(
    () => (canConfirmUnshipped ? new Set(shortfallLines.map((line) => line.id)) : new Set<string>()),
    [shortfallLines, canConfirmUnshipped]
  );

  // Shipped units that left the source and have not arrived. The same status
  // gate as the confirm control: the resolve mutation only accepts a transfer
  // that is still on its way. Offered only to viewers who may manage orders,
  // like every other PO write control.
  const unreceivedLines = useMemo(
    () => unreceivedShippedLines(order, items),
    [order, items]
  );
  const unreceivedItemIds = useMemo(
    // A line already resolved cannot be resolved again (the claim is once per
    // line), so its buttons would always no-op.
    () =>
      canConfirmUnshipped && canResolveUnreceived
        ? new Set(unreceivedLines.filter((line) => line.unreceived_resolved_at == null).map((line) => line.id))
        : new Set<string>(),
    [unreceivedLines, canConfirmUnshipped, canResolveUnreceived]
  );

  const handleResolveUnreceived = async (
    item: PurchaseOrderItem,
    resolution: UnreceivedResolution
  ) => {
    try {
      const result = await resolveUnreceived(order.id, [item.id], resolution);
      setResolveTarget(null);
      // The server's count, never a local guess, as with the confirm control.
      // Zero lines means someone else settled this line first.
      if (result.resolvedLines === 0) {
        toast({
          title: "Already settled",
          description: "These units were already marked returned or scrapped. Nothing changed.",
        });
        return;
      }
      toast({
        title: result.resolution === "returned" ? "Units marked returned" : "Units marked scrapped",
        description: `${result.resolvedUnits.toLocaleString()} units ${result.resolution === "returned" ? "returned to the source location." : "recorded as lost in transit."}`,
      });
    } catch {
      // Error toast is handled by the mutation hook
    }
  };

  const handleConfirmUnshipped = async (item: PurchaseOrderItem) => {
    try {
      const result = await confirmUnshipped(order.id, [item.id]);
      // The server's count, never a local guess: if someone else confirmed
      // this line first it releases nothing, and the toast should say so.
      toast({
        title: "Unshipped units confirmed",
        description: `${result.confirmedUnits.toLocaleString()} units released at the source location.`,
      });
    } catch {
      // Error toast is handled by the mutation hook
    }
  };

  const getRemaining = useCallback(
    (item: PurchaseOrderItem) =>
      receivingBaseline(item, packingListEntered) - item.received_quantity,
    [packingListEntered]
  );

  const parsedQuantities = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of items) {
      const raw = receiveQuantities[item.id];
      if (raw === undefined || raw === "") continue;
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num > 0) {
        map[item.id] = num;
      }
    }
    return map;
  }, [items, receiveQuantities]);

  const itemsToReceive = useMemo(
    () => items.filter((item) => (parsedQuantities[item.id] ?? 0) > 0),
    [items, parsedQuantities]
  );

  const totalReceivingCount = useMemo(
    () => Object.values(parsedQuantities).reduce((sum, qty) => sum + qty, 0),
    [parsedQuantities]
  );

  const handleInputChange = useCallback((itemId: string, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setReceiveQuantities((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  // BLU-584: keep a global keydown → refocus so wedge scanners always land in
  // the scan field when no other input is focused. Paused while a confirm
  // dialog is open or the card is collapsed (no scan field mounted then).
  useScannerAutoFocus({ paused: !isOpen || !!revertConfirmItem || bulkRevertConfirmOpen || !!resolveTarget });

  const flashRow = useCallback((itemId: string) => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    rowRefs.current
      .get(itemId)
      ?.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
    setFlashItemId(itemId);
    if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = window.setTimeout(() => setFlashItemId(null), 900);
  }, []);

  // BLU-584: resolve a scanned SKU to a receivable row and bump its "Receive
  // Now" by 1, respecting the remaining cap. Works for POs and transfers alike
  // (shared card). Match on the base `sku` string (the scanned barcode), then
  // fall back to the design-bearing `shopify_sku` so a variant label still
  // resolves. Returns a result the scan input uses to pick success/error audio.
  const handleScan = useCallback(
    (code: string): ReceivingScanResult => {
      const target = code.trim().toUpperCase();
      if (!target) return "not-found";

      const matches = items.filter((item) => {
        const base = (item.sku ?? "").trim().toUpperCase();
        const variant = ((item as { shopify_sku?: string | null }).shopify_sku ?? "")
          .trim()
          .toUpperCase();
        return base === target || variant === target;
      });

      if (matches.length === 0) {
        toast({
          title: "SKU not on this order",
          description: `No line item matches ${code.trim()}.`,
          variant: "destructive",
        });
        return "not-found";
      }

      // Prefer a row that still has room; otherwise report fully allocated.
      const receivable = matches.find((item) => {
        const remaining = getRemaining(item);
        const current = parsedQuantities[item.id] ?? 0;
        return remaining > 0 && current < remaining;
      });

      if (!receivable) {
        const first = matches[0]!;
        flashRow(first.id);
        toast({
          title: "SKU fully allocated",
          description: `${first.sku} is already at its remaining quantity.`,
          variant: "destructive",
        });
        return "fully-allocated";
      }

      const remaining = getRemaining(receivable);
      const current = parsedQuantities[receivable.id] ?? 0;
      const next = Math.min(current + 1, remaining);
      handleInputChange(receivable.id, String(next));
      flashRow(receivable.id);
      return "allocated";
    },
    [items, parsedQuantities, getRemaining, flashRow, handleInputChange],
  );

  const handleReceiveAll = () => {
    const next: Record<string, string> = {};
    for (const item of items) {
      const remaining = getRemaining(item);
      if (remaining > 0) {
        next[item.id] = String(remaining);
      }
    }
    setReceiveQuantities(next);
  };

  const handleClear = () => {
    setReceiveQuantities({});
  };

  const handleStartEdit = (item: PurchaseOrderItem) => {
    setEditingItemId(item.id);
    setEditQuantity(String(item.received_quantity));
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditQuantity("");
  };

  const handleEditQuantityChange = (value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setEditQuantity(value);
  };

  const handleSaveEdit = async (itemId: string) => {
    const newQty = parseInt(editQuantity, 10);
    if (isNaN(newQty) || newQty < 0) return;

    try {
      await reEditItem({ itemId, newQuantity: newQty });
      setEditingItemId(null);
      setEditQuantity("");
    } catch {
      // Error toast handled by hook
    }
  };

  const toggleHistory = (itemId: string) => {
    setExpandedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (itemsToReceive.length === 0) return;
    setIsSubmitting(true);

    const succeededIds: string[] = [];
    const failedSkus: string[] = [];
    let successUnits = 0;

    try {
      for (const item of itemsToReceive) {
        const qty = parsedQuantities[item.id]!;
        try {
          await processItem({
            itemId: item.id,
            action: "add_stock",
            receivedQuantity: qty,
          });
          succeededIds.push(item.id);
          successUnits += qty;
        } catch (error) {
          reportError(`Error receiving item ${item.sku}`, error);
          failedSkus.push(item.sku || item.id);
        }
      }

      // Settle the receives FIRST, before anything else is attempted. The stock
      // is in; leaving the quantities sitting in the boxes invites a second
      // press and an over-receive.
      if (succeededIds.length > 0) {
        setReceiveQuantities((prev) => {
          const next = { ...prev };
          for (const id of succeededIds) {
            delete next[id];
          }
          return next;
        });
      }

      // Show appropriate toast
      if (failedSkus.length === 0) {
        toast({
          title: "Items received",
          description: `Received ${successUnits.toLocaleString()} units across ${succeededIds.length} SKU${succeededIds.length === 1 ? "" : "s"}`,
        });
      } else if (succeededIds.length > 0) {
        const MAX_SHOWN = 3;
        const skuDisplay = failedSkus.length > MAX_SHOWN
          ? `${failedSkus.slice(0, MAX_SHOWN).join(", ")} and ${failedSkus.length - MAX_SHOWN} more`
          : failedSkus.join(", ");
        toast({
          title: "Some items failed",
          description: `Received ${successUnits.toLocaleString()} units. Failed: ${skuDisplay}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error receiving items",
          description: `All items failed to receive. Please try again.`,
          variant: "destructive",
        });
      }

      // The status flip is a SEPARATE outcome from the receives, so it gets its
      // own try. It shares the backend's completion gate: on a short-shipped
      // transfer the server refuses `received` until the units that never
      // shipped are confirmed. Sharing a catch with the receives turned that
      // refusal into a red "Error receiving items", the quantities stayed in
      // the boxes, and the operator pressed Receive again and over-received.
      // Stock that has already landed is never reported as a failure.
      if (succeededIds.length > 0) {
        const allFullyReceived = items.every((item) => {
          // Items intentionally marked as not received don't block completion
          if (item.processing_status === "not_received") return true;
          // Only credit items that actually succeeded in this batch
          const receiving = succeededIds.includes(item.id)
            ? (parsedQuantities[item.id] ?? 0)
            : 0;
          return (
            item.received_quantity + receiving >=
            receivingBaseline(item, packingListEntered)
          );
        });

        const newStatus = allFullyReceived ? "received" : "partially_received";
        if (order.status !== newStatus) {
          try {
            await updateOrderStatus(order.id, newStatus, order.location_id, {
              suppressErrorToast: true,
            });
          } catch (error) {
            reportError("Error updating order status after receiving", error);
            // A lost status race already got its own gentle notice from the
            // mutation hook, so saying it twice would only add noise.
            const isConflict =
              (error as { data?: { code?: string } } | null)?.data?.code === "CONFLICT";
            if (!isConflict) {
              // Deliberately not destructive: the stock landed, and this is the
              // next step rather than a failure of the one just taken.
              toast({
                title: "Not marked received yet",
                description:
                  order.order_type === "transfer" && shippedQuantitiesRecorded
                    ? "The units you received are saved. Confirm any units that were not shipped, and mark any that never arrived as returned or scrapped, before this transfer can be marked received."
                    : `The units you received are saved, but the order status could not be updated. ${error instanceof Error ? error.message : "Refresh and try again."}`,
              });
            }
            // The receives changed the row, so pull the server's version back
            // instead of leaving the card showing pre-receive numbers.
            void utils.purchaseOrders.getOrder.invalidate();
            void utils.purchaseOrders.getOrders.invalidate();
          }
        }
      }
    } catch (error) {
      reportError("Error receiving items", error);
      toast({
        title: "Error receiving items",
        description: error instanceof Error ? error.message : "An error occurred while receiving items.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (item: PurchaseOrderItem, inputQty: number) => {
    const remaining = getRemaining(item);

    // BLU-535 section D: on a stamped transfer a line that shipped nothing has
    // a baseline of 0, so it reads as "remaining 0" and would wear the same
    // green Received badge as a line that actually arrived. Nothing arrived,
    // and nothing is coming: say so plainly instead.
    if (shippedQuantitiesRecorded && (item.shipped_quantity ?? 0) === 0) {
      return <Badge variant="secondary">Not shipped</Badge>;
    }

    // Shipped units that never arrived and were settled instead of received:
    // say how, rather than wearing the green Received badge.
    if (item.unreceived_resolved_at && remaining <= 0) {
      return (
        <Badge variant="secondary">
          {item.unreceived_resolution === "scrapped" ? "Scrapped" : "Returned"}
        </Badge>
      );
    }

    if (remaining <= 0) {
      return <Badge variant="success">Received</Badge>;
    }
    if (inputQty >= remaining) {
      return <Badge variant="success">Will Complete</Badge>;
    }
    if (inputQty > 0) {
      return <Badge variant="info">Receiving</Badge>;
    }
    // No input yet — show partial vs pending
    if (item.processing_status === "add_stock" || item.processing_status === "edited") {
      return <Badge variant="warning">Partially Received</Badge>;
    }
    return <Badge variant="secondary">Pending</Badge>;
  };

  // ── Variance calculations ──

  const varianceData = useMemo(() => {
    const totalOrdered = items.reduce((sum, i) => sum + i.ordered_quantity, 0);
    const totalShipped = items.reduce((sum, i) => sum + (i.shipped_quantity ?? 0), 0);
    const totalReceived = items.reduce((sum, i) => sum + i.received_quantity, 0);

    const hasShipped = totalShipped > 0;
    const hasReceived = totalReceived > 0;

    // BLU-535 section D. On anything but a transfer that recorded what it
    // shipped this is zero, so the ordinary ordered-against-shipped variance
    // below keeps explaining itself unchanged.
    const totalUnshipped = shortfallUnits(order, items);

    const poToShipped = totalShipped - totalOrdered;
    const shippedToReceived = totalReceived - totalShipped;
    const poToShippedPct = totalOrdered > 0 ? poToShipped / totalOrdered : 0;
    const shippedToReceivedPct = totalShipped > 0 ? shippedToReceived / totalShipped : 0;

    const itemVariances = items.map((item) => {
      const shipped = item.shipped_quantity ?? 0;
      const poVsShipped = shipped - item.ordered_quantity;
      const shippedVsReceived = item.received_quantity - shipped;
      const poVsShippedPct = item.ordered_quantity > 0 ? poVsShipped / item.ordered_quantity : 0;
      const shippedVsReceivedPct = shipped > 0 ? shippedVsReceived / shipped : 0;
      const exceedsThreshold =
        Math.abs(poVsShippedPct) > TOLERANCE_THRESHOLD ||
        Math.abs(shippedVsReceivedPct) > TOLERANCE_THRESHOLD;

      return {
        item,
        shipped,
        poVsShipped,
        shippedVsReceived,
        poVsShippedPct,
        shippedVsReceivedPct,
        exceedsThreshold,
      };
    });

    return {
      totalOrdered,
      totalShipped,
      totalReceived,
      hasShipped,
      hasReceived,
      poToShipped,
      shippedToReceived,
      poToShippedPct,
      shippedToReceivedPct,
      itemVariances,
      totalUnshipped,
      hasVariances: itemVariances.some((v) => v.exceedsThreshold),
    };
  }, [items, order]);

  const handleExportDiscrepancy = () => {
    const headers = [
      "PO_Number", "SKU", "Style", "Colour", "Size",
      "Ordered_Qty", "Shipped_Qty", "Received_Qty",
      "PO_vs_Shipped", "Shipped_vs_Received",
      "PO_vs_Shipped_%", "Shipped_vs_Received_%",
      "Unit_Cost", "PO_vs_Shipped_Cost", "Shipped_vs_Received_Cost",
    ];

    const filteredVariances = showAllVariances
      ? varianceData.itemVariances
      : varianceData.itemVariances.filter((v) => v.exceedsThreshold);

    const rows = filteredVariances.map((v) => {
      const unitCost = v.item.unit_cost ?? 0;
      return [
        order.po_number,
        v.item.sku || "",
        v.item.category || "",
        v.item.color || "",
        v.item.size || "",
        v.item.ordered_quantity.toString(),
        v.shipped.toString(),
        v.item.received_quantity.toString(),
        v.poVsShipped.toString(),
        v.shippedVsReceived.toString(),
        `${(v.poVsShippedPct * 100).toFixed(1)}%`,
        `${(v.shippedVsReceivedPct * 100).toFixed(1)}%`,
        unitCost.toFixed(2),
        (v.poVsShipped * unitCost).toFixed(2),
        (v.shippedVsReceived * unitCost).toFixed(2),
      ];
    });

    const csvContent = toCsvContent([headers, ...rows]);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${order.po_number}-discrepancy-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    toast({
      title: "CSV Downloaded",
      description: `Discrepancy report for ${order.po_number} downloaded`,
    });
  };

  // ── Render ──

  return (
    <>
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none">
            <CardTitle className="flex items-center gap-2 text-base">
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  !isOpen && "-rotate-90"
                )}
              />
              <Package className="h-4 w-4" />
              Receiving
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent>
            {!canReceive ? (
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Submit the purchase order to enable receiving.</p>
                <p>No items have been received yet.</p>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setSelectedTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="ordered" className="gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" />
                    Ordered
                  </TabsTrigger>
                  {shippedTabEditable && (
                    <TabsTrigger value="shipped" className="gap-1.5">
                      <Truck className="h-3.5 w-3.5" />
                      Shipped
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="received" className="gap-1.5">
                    <Package className="h-3.5 w-3.5" />
                    Received
                  </TabsTrigger>
                </TabsList>

                {/* ── Tab 1: Ordered (read-only) ── */}
                <TabsContent value="ordered">
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Ordered</TableHead>
                          <TableHead className="text-right">Unit Cost</TableHead>
                          <TableHead className="text-right">Line Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-1.5">
                                {item.sku}
                                {item.is_unexpected_item && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    Unexpected
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {item.title ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-sm font-medium">{item.title}</span>
                                  {[item.category, item.color, item.size].filter(Boolean).length > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      {[item.category, item.color, item.size].filter(Boolean).join(" · ")}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">
                                  {item.category || "-"} · {item.color || "-"} · {item.size || "-"}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {item.ordered_quantity.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {item.unit_cost != null ? formatCurrency(item.unit_cost) : "-"}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {item.line_cost_total != null ? formatCurrency(item.line_cost_total) : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={2} className="font-medium">Total</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {items.reduce((s, i) => s + i.ordered_quantity, 0).toLocaleString()}
                          </TableCell>
                          <TableCell />
                          <TableCell className="text-right tabular-nums font-medium">
                            {formatCurrency(items.reduce((s, i) => s + (i.line_cost_total ?? 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </TabsContent>

                {/* ── Tab 2: Shipped (packing list entry) ── */}
                {shippedTabEditable && (
                <TabsContent value="shipped">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleFillFromOrdered}
                        disabled={isSavingShipped}
                      >
                        <CheckCheck className="h-4 w-4 mr-2" />
                        Fill from Ordered
                      </Button>
                      {shippedDirty && (
                        <Button
                          size="sm"
                          onClick={handleSaveShipped}
                          disabled={isSavingShipped}
                        >
                          {isSavingShipped ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Truck className="h-4 w-4 mr-2" />
                          )}
                          Save Shipped Quantities
                        </Button>
                      )}
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>SKU</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Ordered</TableHead>
                            <TableHead className="text-right w-[120px]">Shipped</TableHead>
                            <TableHead className="text-right">Variance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((item) => {
                            const shippedVal = parsedShippedQuantities[item.id] ?? item.shipped_quantity ?? 0;
                            const variance = shippedVal - item.ordered_quantity;

                            return (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">{item.sku}</TableCell>
                                <TableCell>
                                  {item.title ? (
                                    <div className="flex flex-col gap-0.5">
                                      <span className="text-sm font-medium">{item.title}</span>
                                      {[item.category, item.color, item.size].filter(Boolean).length > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                          {[item.category, item.color, item.size].filter(Boolean).join(" · ")}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-muted-foreground">
                                      {item.category || "-"} · {item.color || "-"} · {item.size || "-"}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {item.ordered_quantity.toLocaleString()}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    className="h-8 w-[100px] text-right ml-auto tabular-nums"
                                    placeholder="0"
                                    value={shippedQuantities[item.id] ?? ""}
                                    onChange={(e) => handleShippedInputChange(item.id, e.target.value)}
                                    disabled={isSavingShipped}
                                  />
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    "text-right tabular-nums font-medium",
                                    variance === 0 && "text-green-600",
                                    variance < 0 && "text-red-600",
                                    variance > 0 && "text-amber-600"
                                  )}
                                >
                                  {variance === 0 ? "-" : (variance > 0 ? "+" : "") + variance.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </TabsContent>
                )}

                {/* ── Tab 3: Received (existing flow) ── */}
                <TabsContent value="received">
                  <div className="space-y-4">
                    {/* BLU-584: scan a SKU to bump its Receive Now by 1. */}
                    <div className="flex flex-col gap-1.5">
                      <ReceivingScanInput onScan={handleScan} disabled={isSubmitting} />
                      <p className="text-xs text-muted-foreground">
                        Scan a SKU to add one to its Receive Now. Multiple scans accumulate.
                      </p>
                    </div>
                    {items.some(
                      (i) =>
                        (i.processing_status === "add_stock" || i.processing_status === "edited") &&
                        getRemaining(i) > 0
                    ) && (
                      <Alert className="border-amber-200 bg-amber-50 text-amber-700 [&>svg]:text-amber-500">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Some items already have stock recorded. Use the Receive Now column to add more, or the edit button to adjust the total.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReceiveAll}
                        disabled={isSubmitting}
                      >
                        <CheckCheck className="h-4 w-4 mr-2" />
                        Receive All Remaining
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClear}
                        disabled={isSubmitting}
                      >
                        Clear
                      </Button>
                      {canAddSurpriseItem && (
                        <AddUnexpectedItemPopover
                          locationId={orderLocationId}
                          existingSkuIds={existingSkuIds}
                          onAdd={handleAddSurpriseItem}
                          disabled={isSubmitting || isAddingSurpriseItem}
                        />
                      )}
                      {revertableCount > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-auto border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setBulkRevertConfirmOpen(true)}
                          disabled={isReverting}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          {isReverting ? "Reverting..." : `Revert All (${revertableCount} item${revertableCount !== 1 ? 's' : ''})`}
                        </Button>
                      )}
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>SKU</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">
                              {packingListEntered ? "Shipped" : "Ordered"}
                            </TableHead>
                            <TableHead className="text-right">Received</TableHead>
                            <TableHead className="text-right">Remaining</TableHead>
                            <TableHead className="text-right w-[120px]">Receive Now</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((item) => {
                            const remaining = getRemaining(item);
                            const isFullyReceived = remaining <= 0;
                            const inputQty = parsedQuantities[item.id] ?? 0;
                            const isEditing = editingItemId === item.id;
                            const history = Array.isArray(item.processing_history)
                              ? (item.processing_history as Array<Record<string, unknown>>)
                              : [];
                            const hasHistory = history.length > 0;
                            const isExpanded = expandedHistoryIds.has(item.id);

                            return (
                              <Fragment key={item.id}>
                                <TableRow
                                  ref={(el) => {
                                    if (el) rowRefs.current.set(item.id, el);
                                    else rowRefs.current.delete(item.id);
                                  }}
                                  className={cn(
                                    "transition-colors duration-500 motion-reduce:transition-none",
                                    isFullyReceived && !isEditing && "opacity-60",
                                    flashItemId === item.id && "bg-threadheads-purple/10",
                                  )}
                                >

                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-1">
                                      {hasHistory && (
                                        <button
                                          type="button"
                                          onClick={() => toggleHistory(item.id)}
                                          className="p-0.5 rounded hover:bg-muted -ml-1"
                                        >
                                          {isExpanded ? (
                                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                          ) : (
                                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                          )}
                                        </button>
                                      )}
                                      {item.sku}
                                      {item.is_unexpected_item && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                          Unexpected
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {item.title ? (
                                      <div className="flex flex-col gap-0.5">
                                        <span className="text-sm font-medium">{item.title}</span>
                                        {[item.category, item.color, item.size].filter(Boolean).length > 0 && (
                                          <span className="text-xs text-muted-foreground">
                                            {[item.category, item.color, item.size].filter(Boolean).join(" · ")}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-sm text-muted-foreground">
                                        {item.category || "-"} · {item.color || "-"} · {item.size || "-"}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {(packingListEntered ? (item.shipped_quantity ?? 0) : item.ordered_quantity).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {isEditing ? (
                                      <div className="flex flex-col items-end gap-0.5">
                                        <div className="flex items-center gap-1">
                                          <Input
                                            type="text"
                                            inputMode="numeric"
                                            className="h-8 w-[80px] text-right tabular-nums"
                                            value={editQuantity}
                                            onChange={(e) => handleEditQuantityChange(e.target.value)}
                                            autoFocus
                                            disabled={isReEditing}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") handleSaveEdit(item.id);
                                              if (e.key === "Escape") handleCancelEdit();
                                            }}
                                          />
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => handleSaveEdit(item.id)}
                                            disabled={isReEditing}
                                          >
                                            {isReEditing ? (
                                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                              <Check className="h-3.5 w-3.5 text-green-600" />
                                            )}
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={handleCancelEdit}
                                            disabled={isReEditing}
                                          >
                                            <X className="h-3.5 w-3.5 text-muted-foreground" />
                                          </Button>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          Sets total received (not additional)
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-end gap-1">
                                        <span className="tabular-nums">
                                          {item.received_quantity.toLocaleString()}
                                        </span>
                                        {item.received_quantity > 0 && !hasHistory && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                <AlertCircle className="h-3 w-3 text-amber-500 shrink-0" />
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                              <p>No receive history on record</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                        {item.received_quantity > 0 && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => handleStartEdit(item)}
                                              >
                                                <Pencil className="h-3 w-3 text-muted-foreground" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                              <p>Set the total received so far (not additional)</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                        {(item.processing_status === 'add_stock' || item.processing_status === 'edited') && item.received_quantity > 0 && item.unreceived_resolved_at == null && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-red-500 hover:text-red-600"
                                                onClick={() => setRevertConfirmItem(item)}
                                                disabled={isReverting}
                                              >
                                                <RotateCcw className="h-3 w-3" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                              <p>Revert to pending: removes stock from inventory</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {remaining.toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      className="h-8 w-[100px] text-right ml-auto tabular-nums"
                                      placeholder={isFullyReceived ? "-" : "0"}
                                      value={receiveQuantities[item.id] ?? ""}
                                      onChange={(e) => handleInputChange(item.id, e.target.value)}
                                      disabled={isFullyReceived || isSubmitting}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex flex-col items-start gap-1">
                                      {getStatusBadge(item, inputQty)}
                                      {shortfallItemIds.has(item.id) && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 text-xs"
                                          onClick={() => void handleConfirmUnshipped(item)}
                                          disabled={isConfirmingUnshipped}
                                        >
                                          {`Confirm ${(item.ordered_quantity - (item.shipped_quantity ?? 0)).toLocaleString()} were not shipped`}
                                        </Button>
                                      )}
                                      {item.unshipped_confirmed_at && (item.unshipped_quantity ?? 0) > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                          {`${(item.unshipped_quantity ?? 0).toLocaleString()} confirmed not shipped`}
                                        </span>
                                      )}
                                      {unreceivedItemIds.has(item.id) && (
                                        <>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => setResolveTarget({ item, resolution: "returned" })}
                                            disabled={isResolvingUnreceived}
                                          >
                                            {`Mark ${inTransitUnits(item).toLocaleString()} returned to source`}
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => setResolveTarget({ item, resolution: "scrapped" })}
                                            disabled={isResolvingUnreceived}
                                          >
                                            {`Mark ${inTransitUnits(item).toLocaleString()} lost in transit`}
                                          </Button>
                                        </>
                                      )}
                                      {item.unreceived_resolved_at && (item.unreceived_quantity ?? 0) > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                          {item.unreceived_resolution === "scrapped"
                                            ? `${(item.unreceived_quantity ?? 0).toLocaleString()} scrapped, lost in transit`
                                            : `${(item.unreceived_quantity ?? 0).toLocaleString()} returned to source`}
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {hasHistory && isExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={7} className="bg-muted/30 py-2 px-4">
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                                          <History className="h-3 w-3" />
                                          Receive History
                                        </div>
                                        <div className="space-y-1.5">
                                          {history.map((entry, idx) => {
                                            const action = String(entry.action ?? "received");
                                            const actionLabel =
                                              action === "re_edited" ? "Adjusted" :
                                              action === "add_stock" || action === "received" ? "Received" :
                                              action === "not_received" ? "Not Received" :
                                              action === "reverted" ? "Reverted" :
                                              action;
                                            const ts = entry.timestamp
                                              ? new Date(String(entry.timestamp)).toLocaleDateString("en-AU", {
                                                  day: "numeric",
                                                  month: "short",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })
                                              : "";
                                            const qty = typeof entry.received_quantity === "number"
                                              ? entry.received_quantity
                                              : null;
                                            const runningTotal = typeof entry.running_total === "number"
                                              ? entry.running_total
                                              : null;
                                            const notes = entry.notes ? String(entry.notes) : null;

                                            return (
                                              <div
                                                key={idx}
                                                className="flex items-baseline gap-3 text-xs"
                                              >
                                                <span className="text-muted-foreground shrink-0 w-[140px]">
                                                  {ts}
                                                </span>
                                                <Badge
                                                  variant={action === "re_edited" ? "outline" : "secondary"}
                                                  className="text-[10px] px-1.5 py-0"
                                                >
                                                  {actionLabel}
                                                </Badge>
                                                {qty !== null && action !== "not_received" && (
                                                  <span className="tabular-nums font-medium">
                                                    {action === "re_edited" ? (
                                                      <>
                                                        {qty > 0 ? "+" : ""}{qty.toLocaleString()}
                                                        {runningTotal !== null && (
                                                          <span className="text-muted-foreground font-normal">
                                                            {" "}→ {runningTotal.toLocaleString()}
                                                          </span>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <>
                                                        +{qty.toLocaleString()}
                                                        {runningTotal !== null && runningTotal !== qty && (
                                                          <span className="text-muted-foreground font-normal">
                                                            {" "}→ {runningTotal.toLocaleString()}
                                                          </span>
                                                        )}
                                                      </>
                                                    )}
                                                  </span>
                                                )}
                                                {notes && (
                                                  <span className="text-muted-foreground truncate">
                                                    {notes}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {totalReceivingCount > 0 && (
                      <div className="flex items-center justify-between pt-2 border-t">
                        <p className="text-sm text-muted-foreground">
                          Receiving{" "}
                          <span className="font-semibold text-foreground">
                            {totalReceivingCount.toLocaleString()}
                          </span>{" "}
                          {totalReceivingCount === 1 ? "unit" : "units"}
                        </p>
                        <Button onClick={handleSubmit} disabled={isSubmitting}>
                          {isSubmitting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Package className="h-4 w-4 mr-2" />
                          )}
                          Receive Items
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* ── Variance Summary ── */}
                {(varianceData.hasShipped || varianceData.hasReceived || varianceData.totalUnshipped > 0) && (
                  <div className="mt-6 space-y-3 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">Variance Summary</h4>
                      <div className="flex items-center gap-3">
                        {varianceData.hasVariances && (
                          <Button variant="outline" size="sm" onClick={handleExportDiscrepancy}>
                            <Download className="h-4 w-4 mr-2" />
                            Export Discrepancy Report
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="space-y-1">
                        <p className="text-muted-foreground">Total Ordered</p>
                        <p className="text-lg font-semibold tabular-nums">
                          {varianceData.totalOrdered.toLocaleString()}
                        </p>
                      </div>
                      {varianceData.hasShipped && (
                        <div className="space-y-1">
                          <p className="text-muted-foreground">Total Shipped</p>
                          <p className="text-lg font-semibold tabular-nums">
                            {varianceData.totalShipped.toLocaleString()}
                          </p>
                          <p
                            className={cn(
                              "text-xs tabular-nums",
                              varianceData.poToShipped === 0 && "text-green-600",
                              varianceData.poToShipped < 0 && "text-red-600",
                              varianceData.poToShipped > 0 && "text-amber-600"
                            )}
                          >
                            {varianceData.poToShipped === 0
                              ? "Exact match"
                              : `${varianceData.poToShipped > 0 ? "+" : ""}${varianceData.poToShipped.toLocaleString()} (${(varianceData.poToShippedPct * 100).toFixed(1)}%)`}
                          </p>
                        </div>
                      )}
                      {varianceData.hasReceived && (
                        <div className="space-y-1">
                          <p className="text-muted-foreground">Total Received</p>
                          <p className="text-lg font-semibold tabular-nums">
                            {varianceData.totalReceived.toLocaleString()}
                          </p>
                          {varianceData.hasShipped && (
                            <p
                              className={cn(
                                "text-xs tabular-nums",
                                varianceData.shippedToReceived === 0 && "text-green-600",
                                varianceData.shippedToReceived < 0 && "text-red-600",
                                varianceData.shippedToReceived > 0 && "text-amber-600"
                              )}
                            >
                              {varianceData.shippedToReceived === 0
                                ? "Exact match"
                                : `${varianceData.shippedToReceived > 0 ? "+" : ""}${varianceData.shippedToReceived.toLocaleString()} vs shipped (${(varianceData.shippedToReceivedPct * 100).toFixed(1)}%)`}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {varianceData.totalUnshipped > 0 && (
                      <p className="text-sm text-muted-foreground">
                        {`${varianceData.totalUnshipped.toLocaleString()} units not shipped`}
                      </p>
                    )}

                    {varianceData.hasVariances && (
                      <div className="flex items-center gap-2 pt-1">
                        <Switch
                          id="show-all"
                          checked={showAllVariances}
                          onCheckedChange={setShowAllVariances}
                        />
                        <Label htmlFor="show-all" className="text-xs text-muted-foreground cursor-pointer">
                          Show all items in export (not just &gt;5% variance)
                        </Label>
                      </div>
                    )}
                  </div>
                )}
              </Tabs>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>

    {/* Resolve shipped units that never arrived */}
    <Dialog open={!!resolveTarget} onOpenChange={(open) => { if (!open) setResolveTarget(null); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {resolveTarget?.resolution === "scrapped"
              ? "Mark units lost in transit"
              : "Mark units returned to source"}
          </DialogTitle>
          <DialogDescription>
            {resolveTarget &&
              (resolveTarget.resolution === "scrapped"
                ? `${inTransitUnits(resolveTarget.item).toLocaleString()} units of ${resolveTarget.item.sku} will be recorded as scrapped, lost in transit. Stock does not change at either location.`
                : `${inTransitUnits(resolveTarget.item).toLocaleString()} units of ${resolveTarget.item.sku} will go back on hand at ${sourceLocationName || "the source location"}. The destination keeps what it has received.`)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setResolveTarget(null)}>
            Keep waiting
          </Button>
          <Button
            variant={resolveTarget?.resolution === "scrapped" ? "destructive" : "default"}
            onClick={() =>
              resolveTarget && void handleResolveUnreceived(resolveTarget.item, resolveTarget.resolution)
            }
            disabled={isResolvingUnreceived}
          >
            {isResolvingUnreceived && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {resolveTarget?.resolution === "scrapped" ? "Mark scrapped" : "Mark returned"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Line-level revert confirmation */}
    <Dialog open={!!revertConfirmItem} onOpenChange={(open) => { if (!open) setRevertConfirmItem(null); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revert Received Item</DialogTitle>
          <DialogDescription>
            Revert <strong>{revertConfirmItem?.sku}</strong> back to pending.{" "}
            <strong>{(revertConfirmItem as { inventory_adjustment?: number | null } | null)?.inventory_adjustment ?? revertConfirmItem?.received_quantity ?? 0}</strong> units
            will be deducted from inventory. This item will return to the receiving queue.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setRevertConfirmItem(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => revertConfirmItem && handleRevertItem(revertConfirmItem)}
            disabled={isReverting}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {isReverting ? "Reverting..." : "Revert Item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Bulk revert confirmation */}
    <Dialog open={bulkRevertConfirmOpen} onOpenChange={setBulkRevertConfirmOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revert All Received Items</DialogTitle>
          <DialogDescription>
            This will revert <strong>{revertableCount}</strong> item{revertableCount !== 1 ? "s" : ""}{" "}
            (<strong>{revertableTotalUnits}</strong> total units) back to pending.
            Inventory will be deducted for each item. The order status will be updated automatically.
          </DialogDescription>
        </DialogHeader>
        {revertableCount <= 10 && (
          <div className="max-h-48 overflow-auto border rounded-md">
            <Table>
              <TableBody>
                {revertableItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium text-sm py-1.5">{item.sku}</TableCell>
                    <TableCell className="text-right text-sm py-1.5 tabular-nums">
                      -{(item as { inventory_adjustment?: number | null }).inventory_adjustment ?? item.received_quantity} units
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setBulkRevertConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRevertAll}
            disabled={isReverting}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {isReverting ? "Reverting..." : "Revert All"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
